import { mapReferenceToPath } from '@framework/engine/schema/expressions/evaluate/mapReferenceToPath.ts';
import { collectSchemaReferences } from '@framework/engine/schema/expressions/references/collector/collectSchemaReferences.ts';
import {
  type IReference,
  ReferenceType,
} from '@framework/engine/schema/expressions/references/IReference.ts';
import { type IEngineSection } from '@framework/engine/schema/sections/IEngineSection.ts';
import { type IRootWidget } from '@framework/engine/schema/widgets/repository/containers/root/IRootWidget.ts';
import { checkNever } from '@framework/engine/utils/checkNever.ts';
import { type ISerializedFormValue } from '@framework/engine/validator/serialize/serializeFormValues.tsx';
import { get } from 'lodash';
import { type IContextSnapshot } from '../context-snapshot/IContextSnapshot.ts';
import { type IEngineValidationContext } from '../IEngineValidationContext.ts';

export const CONTEXT_SNAPSHOT_KEY = '__context_snapshot';

/**
 * Funzione verbosa ma che ci permette di filtrare le reference da salvare nel
 * context snapshot con la sicurezza che se ci saranno modifiche in futuro a
 * ReferenceType il `checkNever` andrà in errore.
 */
function isContextReference(reference: IReference): boolean {
  switch (reference.type) {
    case ReferenceType.Widget:
    case ReferenceType.VirtualColumn:
      return false; // Non sono riferimenti del contesto di validazione (dati del form)
    case ReferenceType.DossierSemanticFields:
    case ReferenceType.Metadata:
    case ReferenceType.Register:
    case ReferenceType.SemanticFields:
    case ReferenceType.Session:
    case ReferenceType.Sample:
    case ReferenceType.Placeholder:
      return true; // Sono riferimenti del contesto di validazione
    default:
      checkNever(reference.type);
      return false; // Non dovrebbe mai arrivare qui
  }
}

export function serializeContextSnapshot(props: {
  context: IEngineValidationContext | null | undefined;
  configuration: IRootWidget | null | undefined;
}): IContextSnapshot | null {
  const { context, configuration } = props;
  if (!configuration || !context) {
    return null;
  }

  const references = collectSchemaReferences({
    root: configuration,
  });

  // Estraiamo solo le reference non presenti nel form (quindi non widget/virtual column)
  const contextReferences = references.filter(isContextReference);

  const paths = contextReferences.map(mapReferenceToContextPath);

  const serialized = {} as IContextSnapshot;

  paths.forEach(path => {
    const value = get(context, path);
    if (value !== undefined) {
      serialized[path] = value;
    }
  });

  return serialized;
}

/**
 * Funzione che mappa una reference a un valore nel contesto.
 * Usa la "mapReferenceToPath", che fa una cosa simile
 * ma genera il path che serve a _yup_ per risolvere le dipendenze.
 * L'unica differenza è che qui non viene aggiunto il prefisso `$`.
 * È il percorso reale dentro l'oggetto del contesto.
 * I widget vengono ignorati, dato che non fanno parte del contesto di validazione
 * come oggetti ma sono già all'interno dei values.
 */
function mapReferenceToContextPath(reference: IReference): string {
  const result = mapReferenceToPath(reference);
  if (result.startsWith('$')) {
    return result.slice(1); // Rimuove il prefisso $
  }
  return result;
}

/**
 * Oggetto chiave/valore del context snapshot serializzato pronto per essere salvato
 * nei valori della sezione.
 */
function getContextSnapshotSerializedData(
  value: ISerializedFormValue,
  sections: IEngineSection[],
  context: IEngineValidationContext | null | undefined,
) {
  return {
    [CONTEXT_SNAPSHOT_KEY]: serializeContextSnapshot({
      context,
      configuration: sections.find(s => s.id === value?.name)?.ultimaVersione
        ?.elementi,
    }),
  };
}

/**
 * Transform utilizzata nella serializzazione dei valori del form per aggiungere
 * i dati relativi al context snapshot a ogni sezione.
 */
export function addContextSnapshotToFormValuesFn(
  sections: IEngineSection[],
  context: IEngineValidationContext | null | undefined,
) {
  return (values: ISerializedFormValue): ISerializedFormValue =>
    ({
      ...values,
      ...getContextSnapshotSerializedData(values, sections, context),
    }) as any;
}
